import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Typography } from '@mui/material';

import type { ReportEmployer, ReportSubOwner } from '../../../../../../../../types';

import { TranslationKeys } from '../../../../../../../../translations';

import {
  ActionLink,
  ListCollapsibleRow,
  ListRow,
  ListRowNoData,
  ListRowSkeleton,
  Pagination,
} from '../../../../../../../../components';

import type { UserAccessesListOnChangeSelected, UserAccessesListSelectedData } from '../../UserAccessesList.types';

import { UserAccessesListHeader } from '../UserAccessesListHeader';
import { UserAccessesListContainer } from '../UserAccessesListContainer';
import { MuiCheckboxStyled } from '../../../../../../../../components/Checkbox/Checkbox.styles';

type UserAccessesListSubOwnersProps = {
  isLoading?: boolean;
  isLoggedInUser?: boolean;
  onChangeSelected: UserAccessesListOnChangeSelected;
  selectedData: UserAccessesListSelectedData | undefined;
  subOwners: Array<ReportSubOwner> | undefined;
  selectedEventFlows: Array<string> | undefined;
  openConfirmationDialog: (value: boolean) => void;
  topLevelEmployers?: Array<ReportEmployer>;
  page?: number;
  limit?: number;
  totalItems?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
};

const getAllEmployerIds = (subOwner: ReportSubOwner): string[] => {
  const ids: string[] = [];
  subOwner.employers?.forEach(employer => {
    ids.push(employer.employerId);
    employer.subEmployers?.forEach(subEmp => ids.push(subEmp.employerId));
  });
  return ids;
};

const isSubOwnerSelectedHelper = (
  selectedData: UserAccessesListSelectedData | undefined,
  subOwnerId: string,
): boolean => {
  const subOwners = selectedData?.subOwners;
  if (subOwners === '*') return true;
  if (typeof subOwners === 'object') return !!subOwners?.[subOwnerId];
  return false;
};

const getSubOwnersAsObject = (
  subOwners: string | Record<string, string | Record<string, string>> | undefined,
): Record<string, string | Record<string, string>> => {
  if (subOwners === '*' || typeof subOwners === 'string') return {};
  return subOwners || {};
};

const isEmployerInSubOwnerSelectedHelper = (
  selectedData: UserAccessesListSelectedData | undefined,
  employerId: string,
): boolean => {
  const employers = selectedData?.employers;
  if (employers === '*') return true;
  if (typeof employers === 'object') return !!employers?.[employerId];
  return false;
};

const getEmployerWithSubEmployerIds = (employer: ReportEmployer): string[] => {
  const ids = [employer.employerId];
  employer.subEmployers?.forEach(subEmp => ids.push(subEmp.employerId));
  return ids;
};

const getEmployersAsObject = (
  employers: string | Record<string, string | Record<string, string>> | undefined,
): Record<string, string | Record<string, string>> => {
  if (employers === '*' || typeof employers === 'string') return {};
  return employers || {};
};

export const UserAccessesListSubOwners = React.memo(function UserAccessesListSubOwners({
  subOwners,
  selectedData,
  onChangeSelected,
  selectedEventFlows = [],
  openConfirmationDialog,
  isLoggedInUser = false,
  isLoading = false,
  topLevelEmployers,
  page = 1,
  limit = 25,
  totalItems = 0,
  totalPages = 0,
  onPageChange,
  onLimitChange,
  searchQuery: externalSearchQuery = '',
  onSearchQueryChange,
}: UserAccessesListSubOwnersProps) {
  const searchQueryRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedDataRef = React.useRef(selectedData);
  const openConfirmationDialogRef = React.useRef(openConfirmationDialog);

  React.useEffect(() => {
    selectedDataRef.current = selectedData;
  }, [selectedData]);

  React.useEffect(() => {
    openConfirmationDialogRef.current = openConfirmationDialog;
  }, [openConfirmationDialog]);

  const listData = subOwners;

  const totalSelectedSubOwners =
    selectedData?.subOwners === '*'
      ? totalItems
      : Object.keys((selectedData?.subOwners as Record<string, string>) || {}).length;

  const disabledIfLoadingOrIsLoggedInUser = isLoggedInUser || isLoading;
  const hasData = listData && listData?.length > 0;
  const showData = !isLoading && hasData;
  const showNoData = !isLoading && !showData;

  const onChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (searchQueryRef.current) {
      clearTimeout(searchQueryRef.current);
    }
    searchQueryRef.current = setTimeout(() => {
      onSearchQueryChange?.(value);
      onPageChange?.(1);
    }, 300);
  };

  const onPaginationChange = React.useCallback(
    ({ page: newPage, rowSize }: { page?: number; rowSize?: number }) => {
      if (newPage !== undefined) {
        onPageChange?.(newPage);
      }
      if (rowSize !== undefined) {
        onLimitChange?.(rowSize);
        onPageChange?.(1);
      }
    },
    [onPageChange, onLimitChange],
  );

  const topLevelEmployerIdsSet = React.useMemo(() => {
    const ids = new Set<string>();
    topLevelEmployers?.forEach(employer => {
      ids.add(employer.employerId);
      employer.subEmployers?.forEach(subEmp => ids.add(subEmp.employerId));
    });
    return ids;
  }, [topLevelEmployers]);

  const onSelectAll = React.useCallback(() => {
    const currentSelectedData = selectedDataRef.current;
    if (!currentSelectedData) return;

    const shouldSelectAll = currentSelectedData.subOwners !== '*';

    if (shouldSelectAll) {
      onChangeSelected({
        ...currentSelectedData,
        areAllSubOwnersSelected: true,
        subOwners: '*',
        eventFlows: [],
      });
    } else {
      const currentEmployers = getEmployersAsObject(currentSelectedData.employers);
      const newEmployers: Record<string, string | Record<string, string>> = {};

      const preserveAllTopLevel = currentSelectedData.areAllEmployersSelected;

      if (preserveAllTopLevel) {
        topLevelEmployerIdsSet.forEach(employerId => {
          newEmployers[employerId] = '*';
        });
      } else {
        Object.keys(currentEmployers).forEach(employerId => {
          if (topLevelEmployerIdsSet.has(employerId)) {
            newEmployers[employerId] = currentEmployers[employerId];
          }
        });
      }

      onChangeSelected({
        ...currentSelectedData,
        areAllSubOwnersSelected: false,
        subOwners: {},
        employers: newEmployers,
        eventFlows: [],
      });
    }
  }, [onChangeSelected, topLevelEmployerIdsSet]);

  const onSelectAllOnPage = React.useCallback(() => {
    const currentSelectedData = selectedDataRef.current;
    if (!currentSelectedData) return;

    const allOnPageSelected = subOwners?.every(subOwner =>
      isSubOwnerSelectedHelper(currentSelectedData, subOwner.subOwnerId),
    );

    const shouldSelectAllOnPage = !allOnPageSelected;

    const newSubOwners = getSubOwnersAsObject(currentSelectedData.subOwners);
    const currentEmployers = getEmployersAsObject(currentSelectedData.employers);
    const newEmployers: Record<string, string | Record<string, string>> = { ...currentEmployers };
    let newEventFlows = [...(currentSelectedData.eventFlows || [])];

    if (shouldSelectAllOnPage && subOwners) {
      subOwners.forEach(subOwner => {
        newSubOwners[subOwner.subOwnerId] = '*';
        subOwner.employers?.forEach(employer => {
          newEmployers[employer.employerId] = '*';
          employer.subEmployers?.forEach(subEmp => {
            newEmployers[subEmp.employerId] = '*';
          });
        });
      });
    } else {
      subOwners?.forEach(subOwner => {
        delete newSubOwners[subOwner.subOwnerId];

        const employerIds = getAllEmployerIds(subOwner);
        employerIds.forEach(employerId => {
          if (!topLevelEmployerIdsSet.has(employerId)) {
            delete newEmployers[employerId];
            newEventFlows = newEventFlows.filter(id => id !== employerId);
          }
        });
      });
    }

    onChangeSelected({
      ...currentSelectedData,
      areAllSubOwnersSelected: false,
      subOwners: newSubOwners,
      employers: newEmployers,
      eventFlows: newEventFlows,
    });
  }, [onChangeSelected, subOwners, topLevelEmployerIdsSet]);

  const onSelectSubOwner = React.useCallback(
    (subOwner: ReportSubOwner) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const subOwnerId = subOwner.subOwnerId;
      const isSelected = isSubOwnerSelectedHelper(currentSelectedData, subOwnerId);
      const newSubOwners = getSubOwnersAsObject(currentSelectedData.subOwners);
      const newEmployers = getEmployersAsObject(currentSelectedData.employers);
      let newEventFlows = [...(currentSelectedData.eventFlows || [])];

      if (isSelected) {
        delete newSubOwners[subOwnerId];
        const employerIds = getAllEmployerIds(subOwner);
        employerIds.forEach(id => {
          if (!topLevelEmployerIdsSet.has(id)) {
            delete newEmployers[id];
          }
        });
        newEventFlows = newEventFlows.filter(id => !employerIds.includes(id) || topLevelEmployerIdsSet.has(id));
      } else {
        newSubOwners[subOwnerId] = '*';
        subOwner.employers?.forEach(employer => {
          newEmployers[employer.employerId] = '*';
          employer.subEmployers?.forEach(subEmp => {
            newEmployers[subEmp.employerId] = '*';
          });
        });
      }

      onChangeSelected({
        ...currentSelectedData,
        areAllSubOwnersSelected: false,
        subOwners: newSubOwners,
        employers: newEmployers,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected, topLevelEmployerIdsSet],
  );

  const onSelectEventFlow = React.useCallback(
    (employerId: string, eventFlowDefaultUser: string | undefined) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const currentEventFlows = currentSelectedData.eventFlows || [];
      const isSelected = currentEventFlows.includes(employerId);

      const newEventFlows = isSelected
        ? currentEventFlows.filter(id => id !== employerId)
        : [...currentEventFlows, employerId];

      openConfirmationDialogRef.current(eventFlowDefaultUser != null);

      onChangeSelected({
        ...currentSelectedData,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const onSelectEmployerInSubOwner = React.useCallback(
    (subOwner: ReportSubOwner, employer: ReportEmployer) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const isSelected = isEmployerInSubOwnerSelectedHelper(currentSelectedData, employer.employerId);
      const newSubOwners = getSubOwnersAsObject(currentSelectedData.subOwners);
      const newEmployers = getEmployersAsObject(currentSelectedData.employers);
      let newEventFlows = [...(currentSelectedData.eventFlows || [])];

      if (isSelected) {
        const idsToRemove = getEmployerWithSubEmployerIds(employer);
        idsToRemove.forEach(id => delete newEmployers[id]);
        newEventFlows = newEventFlows.filter(id => !idsToRemove.includes(id));
      } else {
        newSubOwners[subOwner.subOwnerId] = '*';
        newEmployers[employer.employerId] = '*';
        employer.subEmployers?.forEach(subEmp => {
          newEmployers[subEmp.employerId] = '*';
        });
      }

      onChangeSelected({
        ...currentSelectedData,
        subOwners: newSubOwners,
        employers: newEmployers,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const onSelectSubEmployerInSubOwner = React.useCallback(
    (subOwner: ReportSubOwner, parentEmployer: ReportEmployer, subEmployerId: string) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const isSubEmployerSelected = isEmployerInSubOwnerSelectedHelper(currentSelectedData, subEmployerId);
      const newSubOwners = getSubOwnersAsObject(currentSelectedData.subOwners);
      const newEmployers = getEmployersAsObject(currentSelectedData.employers);
      let newEventFlows = [...(currentSelectedData.eventFlows || [])];

      if (isSubEmployerSelected) {
        delete newEmployers[subEmployerId];
        newEventFlows = newEventFlows.filter(id => id !== subEmployerId);
      } else {
        newSubOwners[subOwner.subOwnerId] = '*';
        newEmployers[parentEmployer.employerId] = '*';
        newEmployers[subEmployerId] = '*';
      }

      onChangeSelected({
        ...currentSelectedData,
        subOwners: newSubOwners,
        employers: newEmployers,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const renderSubEmployers = React.useCallback(
    (subOwner: ReportSubOwner, employer: ReportEmployer) => {
      const isSubOwnerSelected = isSubOwnerSelectedHelper(selectedData, subOwner.subOwnerId);

      return employer.subEmployers?.map(subEmployer => {
        const isSelected =
          isSubOwnerSelected || isEmployerInSubOwnerSelectedHelper(selectedData, subEmployer.employerId);
        const isEventFlowSelected = selectedEventFlows.includes(subEmployer.employerId);

        return (
          <ListRow
            key={`${subOwner.subOwnerId}_${employer.employerId}_${subEmployer.employerId}`}
            selected={isSelected}
            onSelect={onSelectSubEmployerInSubOwner(subOwner, employer, subEmployer.employerId)}
            disabled={disabledIfLoadingOrIsLoggedInUser}
            innerRow
          >
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
              <Typography>
                <ActionLink to={`/employers/${subEmployer.employerId}`}>{subEmployer.name}</ActionLink>
              </Typography>
            </Box>
            <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
              <MuiCheckboxStyled
                color={'secondary'}
                onClick={onSelectEventFlow(subEmployer.employerId, subEmployer.eventFlowDefaultUser)}
                checked={isEventFlowSelected && isSelected}
                disabled={!isSelected || disabledIfLoadingOrIsLoggedInUser}
              />
              <Typography variant={'caption'} textTransform={'initial'}>
                <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
              </Typography>
            </Typography>
          </ListRow>
        );
      });
    },
    [
      selectedData,
      selectedEventFlows,
      onSelectSubEmployerInSubOwner,
      onSelectEventFlow,
      disabledIfLoadingOrIsLoggedInUser,
    ],
  );

  const renderSubOwnerEmployers = React.useCallback(
    (subOwner: ReportSubOwner) => {
      const isSubOwnerSelected = isSubOwnerSelectedHelper(selectedData, subOwner.subOwnerId);

      return subOwner.employers.map(employer => {
        const isSelected = isSubOwnerSelected || isEmployerInSubOwnerSelectedHelper(selectedData, employer.employerId);
        const isEventFlowSelected = selectedEventFlows.includes(employer.employerId);
        const hasSubEmployers = employer.subEmployers && employer.subEmployers.length > 0;

        const selectedSubEmployerCount = hasSubEmployers
          ? employer.subEmployers!.filter(
              subEmp => isSubOwnerSelected || isEmployerInSubOwnerSelectedHelper(selectedData, subEmp.employerId),
            ).length
          : 0;

        const rowContent = (
          <>
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
              <Typography>
                <ActionLink to={`/employers/${employer.employerId}`}>{employer.name}</ActionLink>
              </Typography>
              {hasSubEmployers && (
                <Typography variant={'caption'} textTransform={'initial'}>
                  <FormattedMessage
                    id={TranslationKeys.users_accessesList_subEmployers}
                    values={{ count: employer.subEmployers?.length }}
                  />
                </Typography>
              )}
              {selectedSubEmployerCount > 0 && (
                <Typography variant={'caption'} textTransform={'initial'}>
                  <FormattedMessage
                    id={TranslationKeys.users_accessesList_selectedCountEmployers}
                    values={{ count: selectedSubEmployerCount }}
                  />
                </Typography>
              )}
            </Box>
            <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
              <MuiCheckboxStyled
                color={'secondary'}
                onClick={onSelectEventFlow(employer.employerId, employer.eventFlowDefaultUser)}
                checked={isEventFlowSelected && isSelected}
                disabled={!isSelected || disabledIfLoadingOrIsLoggedInUser}
              />
              <Typography variant={'caption'} textTransform={'initial'}>
                <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
              </Typography>
            </Typography>
          </>
        );

        if (hasSubEmployers) {
          return (
            <ListCollapsibleRow
              key={`${subOwner.subOwnerId}_${employer.employerId}`}
              selected={isSelected}
              onSelect={onSelectEmployerInSubOwner(subOwner, employer)}
              disabled={disabledIfLoadingOrIsLoggedInUser}
              rowHeader={rowContent}
            >
              {renderSubEmployers(subOwner, employer)}
            </ListCollapsibleRow>
          );
        }

        return (
          <ListRow
            key={`${subOwner.subOwnerId}_${employer.employerId}`}
            selected={isSelected}
            onSelect={onSelectEmployerInSubOwner(subOwner, employer)}
            disabled={disabledIfLoadingOrIsLoggedInUser}
            innerRow
          >
            {rowContent}
          </ListRow>
        );
      });
    },
    [
      selectedData,
      selectedEventFlows,
      disabledIfLoadingOrIsLoggedInUser,
      onSelectEventFlow,
      onSelectEmployerInSubOwner,
      renderSubEmployers,
    ],
  );

  return (
    <Box sx={theme => ({ marginTop: theme.spacing(2) })}>
      <UserAccessesListHeader
        headerTextId={TranslationKeys.users_accessesList_subOwners}
        onChange={onChangeSearchInput}
        isLoading={isLoading}
        disabled={disabledIfLoadingOrIsLoggedInUser}
        onSelectAll={onSelectAll}
        onSelectAllOnPage={onSelectAllOnPage}
        areAllSelected={selectedData?.subOwners === '*'}
        areAllOnPageSelected={
          !!subOwners?.length &&
          subOwners?.every(subOwner => isSubOwnerSelectedHelper(selectedData, subOwner.subOwnerId))
        }
        noData={listData?.length === 0}
        count={totalSelectedSubOwners}
        totalCount={totalItems}
      />

      <UserAccessesListContainer>
        {isLoading && <ListRowSkeleton />}

        {showData &&
          listData?.map(subOwner => {
            const hasEmployers = subOwner.employers?.length > 0;
            const isSelected = isSubOwnerSelectedHelper(selectedData, subOwner.subOwnerId);

            return (
              <ListCollapsibleRow
                key={subOwner.subOwnerId}
                selected={isSelected}
                onSelect={onSelectSubOwner(subOwner)}
                disabled={disabledIfLoadingOrIsLoggedInUser}
                childrenExtraLeftPadding={!hasEmployers}
                rowHeader={
                  <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={1}>
                    <Typography>
                      <ActionLink to={`/sub-owners/${subOwner.subOwnerId}`}>{subOwner.name}</ActionLink>
                    </Typography>
                    {hasEmployers && (
                      <Typography variant={'caption'} textTransform={'initial'}>
                        <FormattedMessage
                          id={TranslationKeys.users_accessesList_subEmployers}
                          values={{ count: subOwner.employers.length }}
                        />
                      </Typography>
                    )}
                  </Box>
                }
              >
                {hasEmployers ? (
                  renderSubOwnerEmployers(subOwner)
                ) : (
                  <ListRowNoData searchQuery={externalSearchQuery} innerRow />
                )}
              </ListCollapsibleRow>
            );
          })}

        {showNoData && <ListRowNoData searchQuery={externalSearchQuery} />}
      </UserAccessesListContainer>

      {/* Pagination - only show for non-super-admin users with server-side pagination */}
      {totalPages > 0 && (
        <Pagination
          count={totalItems}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPaginationChange={onPaginationChange}
        />
      )}
    </Box>
  );
});
